import type React from "react"
import { useSelector, useDispatch } from "react-redux"
import { useEffect, useState } from "react"
import type { RootState } from "../store/store"
import type { Order } from "../store/slices/ordersSlice"
import { addToCart, clearCart } from "../store/slices/cartSlice"
import { Package, Truck, CheckCircle, Clock, XCircle, Eye, RotateCcw, X, MapPin, Phone, User, CreditCard } from 'lucide-react'
import { realtimeService } from "../services/firebase"
import { Link, useNavigate } from "react-router-dom"
import LazyImage from "../components/common/LazyImage"
import toast from "react-hot-toast"
import { collection, getDocs, query as firestoreQuery, where, doc, getDoc } from "firebase/firestore"
import { db } from "../firebase/config"

// Helper to slugify strings for URLs - MUST match ProductDetail logic
function slugify(str: string | undefined | null): string {
  if (!str) return ""
  return String(str).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
}

// Function to find product in Firestore and generate proper URL
const findProductAndGenerateUrl = async (item: Order["items"][number]): Promise<string> => {
  try {
    const productDocRef = doc(db, "products", item.id)
    const productDoc = await getDoc(productDocRef)

    if (productDoc.exists()) {
      const productData = productDoc.data() as any
      const primaryCategory = productData.categories?.[0] || productData.category || "furniture"
      const categorySlug = slugify(primaryCategory)
      const nameSlug = slugify(productData.name) || item.id
      const url = `/${categorySlug}/${nameSlug}`
      return url
    }

    const productsRef = collection(db, "products")
    const productsQuery = firestoreQuery(productsRef, where("isActive", "==", true), where("name", "==", item.name))
    const productsSnapshot = await getDocs(productsQuery)

    if (!productsSnapshot.empty) {
      const productDoc = productsSnapshot.docs[0]
      const productData = productDoc.data() as any
      const primaryCategory = productData.categories?.[0] || productData.category || "furniture"
      const categorySlug = slugify(primaryCategory)
      const nameSlug = slugify(productData.name) || productDoc.id
      const url = `/${categorySlug}/${nameSlug}`
      return url
    }

    let primaryCategory = (item as any).category
    if (!primaryCategory && item.name) {
      const itemName = item.name.toLowerCase()
      if (itemName.includes("bed") && !itemName.includes("sofa")) {
        primaryCategory = "beds"
      } else if (itemName.includes("sofa") || itemName.includes("couch")) {
        primaryCategory = "sofas"
      } else if (itemName.includes("chair") && !itemName.includes("sofa")) {
        primaryCategory = "chairs"
      } else if (itemName.includes("dining") && (itemName.includes("table") || itemName.includes("set"))) {
        primaryCategory = "dining-sets"
      } else if (itemName.includes("table") && !itemName.includes("dining")) {
        primaryCategory = "tables"
      } else if (itemName.includes("wardrobe") || itemName.includes("closet")) {
        primaryCategory = "wardrobes"
      } else if (itemName.includes("door") || itemName.includes("safety")) {
        primaryCategory = "doors"
      } else if (itemName.includes("tv") || itemName.includes("entertainment")) {
        primaryCategory = "tv-units"
      } else if (itemName.includes("cupboard") || itemName.includes("cabinet")) {
        primaryCategory = "storage"
      } else {
        primaryCategory = "furniture"
      }
    }

    if (!primaryCategory) {
      primaryCategory = "furniture"
    }

    const categorySlug = slugify(primaryCategory)
    const nameSlug = slugify(item.name) || item.id
    const fallbackUrl = `/${categorySlug}/${nameSlug}`
    return fallbackUrl
  } catch (error) {
    const categorySlug = slugify((item as any).category) || "furniture"
    const nameSlug = slugify(item.name) || item.id
    return `/${categorySlug}/${nameSlug}`
  }
}

interface OrderDetailsModalProps {
  order: Order | null
  isOpen: boolean
  onClose: () => void
  onBuyAllAgain: (order: Order) => void
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, isOpen, onClose, onBuyAllAgain }) => {
  if (!isOpen || !order) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Order Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Order Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Order ID:</span>
                <span className="font-medium text-sm">#{order.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Order Date:</span>
                <span className="font-medium">{new Date(order.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status:</span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    order.status === "delivered"
                      ? "bg-green-100 text-green-800"
                      : order.status === "shipped"
                      ? "bg-purple-100 text-purple-800"
                      : order.status === "confirmed"
                      ? "bg-blue-100 text-blue-800"
                      : order.status === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Payment Status:</span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    order.paymentStatus === "completed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {order.paymentStatus === "completed" ? "Paid" : "Pending"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Amount:</span>
                <span className="text-xl font-bold text-primary-600">₹{order.total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Shipping Address
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2 text-gray-500" />
                <span className="font-medium">{order.shippingAddress?.name}</span>
              </div>
              <div className="flex items-start">
                <MapPin className="w-4 h-4 mr-2 text-gray-500 mt-0.5" />
                <div>
                  <div>{order.shippingAddress?.address}</div>
                  <div>
                    {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.pincode}
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2 text-gray-500" />
                <span>{order.shippingAddress?.phone}</span>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item: Order["items"][number]) => (
                <div key={`${item.id}-${(item as any).variantId || "no-variant"}`} className="flex items-center space-x-4 bg-gray-50 rounded-lg p-3">
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                    <LazyImage
                      src={item.image || "/placeholder.svg"}
                      alt={item.name || "Product"}
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                      width={64}
                      height={64}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
                    {/* Variant details */}
                    {((item as any).size || (item as any).color) && (
                      <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
                        {(item as any).size ? <span>Size: {(item as any).size}</span> : null}
                        {(item as any).color ? (
                          <span className="inline-flex items-center gap-1">
                            <span>Color:</span>
                            <span
                              className="inline-block w-3 h-3 rounded-full border"
                              style={{ backgroundColor: (item as any).colorHex || "#ccc" }}
                              title={(item as any).color}
                              aria-label={(item as any).color}
                            />
                            <span>{(item as any).color}</span>
                          </span>
                        ) : null}
                      </div>
                    )}
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                    <p className="text-sm text-gray-600">Price: ₹{item.price.toLocaleString()} each</p>
                  </div>
                  <p className="font-semibold text-gray-900 flex-shrink-0">₹{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Payment Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Method:</span>
                <span className="font-medium">{(order as any).paymentMethod || "Online Payment"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-medium text-sm">{(order as any).transactionId || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => onBuyAllAgain(order)}
              className="flex-1 bg-accent-500 text-black py-2 px-4 rounded-lg hover:bg-accent-600 transition-colors flex items-center justify-center"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Buy All Again
            </button>
            <button onClick={onClose} className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const Orders: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { orders: reduxOrders } = useSelector((state: RootState) => state.orders)
  const [orders, setOrders] = useState<Order[]>(reduxOrders)
  const [filterPeriod, setFilterPeriod] = useState<"past_month" | "past_3_months" | "all">("past_3_months")
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (!user?.uid) return
    const unsubscribe = realtimeService.onUserOrdersChange(user.uid, (newOrders) => {
      setOrders(newOrders)
    })
    return () => unsubscribe()
  }, [user?.uid])

  const userOrders = orders.filter((order: Order) => order.userId === user?.uid)

  const getFilteredOrders = () => {
    const now = new Date()
    const cutoffDate = new Date()
    switch (filterPeriod) {
      case "past_month":
        cutoffDate.setMonth(now.getMonth() - 1)
        break
      case "past_3_months":
        cutoffDate.setMonth(now.getMonth() - 3)
        break
      case "all":
        return userOrders
    }
    return userOrders.filter((order) => {
      const orderDate = new Date(order.createdAt)
      return orderDate >= cutoffDate
    })
  }

  const filteredOrders = getFilteredOrders()

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-500" />
      case "confirmed":
        return <CheckCircle className="w-5 h-5 text-blue-500" />
      case "shipped":
        return <Truck className="w-5 h-5 text-purple-500" />
      case "delivered":
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case "cancelled":
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <Package className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "confirmed":
        return "bg-blue-100 text-blue-800"
      case "shipped":
        return "bg-purple-100 text-purple-800"
      case "delivered":
        return "bg-green-100 text-green-800"
      case "cancelled":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order)
    setIsModalOpen(true)
  }

  const handleViewItem = async (item: Order["items"][number], e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!item.name || !item.id) {
      console.error("Invalid item data for navigation:", item)
      toast.error("Unable to view item - invalid product data")
      return
    }

    try {
      const loadingToast = toast.loading("Finding product...")
      const url = await findProductAndGenerateUrl(item)
      toast.dismiss(loadingToast)

      if (!url || url === "//" || url === "/furniture/" || url.length < 3) {
        toast.error("Unable to view item - could not generate valid URL")
        const searchUrl = `/search?q=${encodeURIComponent(item.name)}`
        navigate(searchUrl)
        return
      }

      navigate(url)
      toast.success("Opening product page...")
    } catch (error) {
      console.error("Error in handleViewItem:", error)
      toast.error("Failed to find product")
      if (item.name) {
        const searchUrl = `/search?q=${encodeURIComponent(item.name)}`
        navigate(searchUrl)
      }
    }
  }

  const handleBuyAgain = (item: Order["items"][number], e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      toast.error("Please login to continue")
      navigate("/login")
      return
    }

    try {
      // Convert order item back into cart item with variant details preserved
      const perUnitPrice = item.price // already variant-adjusted if applicable

      dispatch(clearCart())

      for (let i = 0; i < item.quantity; i++) {
        dispatch(
          addToCart({
            id: item.id,
            name: item.name,
            price: perUnitPrice,
            basePrice: (item as any).basePrice,
            priceModifier: (item as any).priceModifier,
            image: item.image,
            images: [item.image].filter(Boolean),
            category: (item as any).category || "furniture",
            quantity: 1,
            variant:
              (item as any).variantId || (item as any).size || (item as any).color
                ? {
                    id: (item as any).variantId,
                    size: (item as any).size,
                    color: (item as any).color,
                    colorHex: (item as any).colorHex,
                    priceModifier: (item as any).priceModifier,
                  }
                : undefined,
            size: (item as any).size,
            color: (item as any).color,
            colorHex: (item as any).colorHex,
            requiresVariant: Boolean((item as any).variantId),
          }),
        )
      }

      toast.success(`Added ${item.name} to cart`)
      navigate("/checkout")
    } catch (error) {
      console.error("Error adding item to cart:", error)
      toast.error("Failed to add item to cart")
    }
  }

  const handleBuyAllAgain = (order: Order, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!user) {
      toast.error("Please login to continue")
      navigate("/login")
      return
    }

    try {
      dispatch(clearCart())

      order.items.forEach((item) => {
        for (let i = 0; i < item.quantity; i++) {
          dispatch(
            addToCart({
              id: item.id,
              name: item.name,
              price: item.price,
              basePrice: (item as any).basePrice,
              priceModifier: (item as any).priceModifier,
              image: item.image,
              images: [item.image].filter(Boolean),
              category: (item as any).category || "furniture",
              quantity: 1,
              variant:
                (item as any).variantId || (item as any).size || (item as any).color
                  ? {
                      id: (item as any).variantId,
                      size: (item as any).size,
                      color: (item as any).color,
                      colorHex: (item as any).colorHex,
                      priceModifier: (item as any).priceModifier,
                    }
                  : undefined,
              size: (item as any).size,
              color: (item as any).color,
              colorHex: (item as any).colorHex,
              requiresVariant: Boolean((item as any).variantId),
            }),
          )
        }
      })

      toast.success(`Added ${order.items.length} items to cart`)
      navigate("/checkout")
    } catch (error) {
      console.error("Error adding items to cart:", error)
      toast.error("Failed to add items to cart")
    }
  }

  if (filteredOrders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Package className="w-20 h-20 sm:w-24 sm:h-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">No orders found</h2>
          <p className="text-gray-600 mb-8">
            {filterPeriod === "all" ? "When you place orders, they will appear here." : "No orders found for the selected period."}
          </p>
          {filterPeriod !== "all" && (
            <button onClick={() => setFilterPeriod("all")} className="text-primary-600 hover:text-primary-700 font-medium">
              View all orders
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Header with Filter */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Orders</h1>

          {/* Filter Dropdown */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <span className="text-gray-600 text-sm">
              {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""} placed in
            </span>
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value as typeof filterPeriod)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-auto"
            >
              <option value="past_month">past month</option>
              <option value="past_3_months">past 3 months</option>
              <option value="all">all time</option>
            </select>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {filteredOrders.map((order: Order) => (
            <div key={order.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              {/* Order Header - Clickable */}
              <div className="p-4 sm:p-6 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => handleOrderClick(order)}>
                <div className="flex flex-col space-y-3 sm:space-y-4">
                  {/* Order ID and Status Row */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">Order #{order.id}</h3>
                      <p className="text-sm text-gray-600 mt-1">Placed on {new Date(order.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                      {getStatusIcon(order.status)}
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${getStatusColor(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 truncate">
                        Delivery Address: {order.shippingAddress.address}, {order.shippingAddress.city}
                      </p>
                      <p className="text-sm text-gray-600">Payment: {order.paymentStatus === "completed" ? "Paid" : "Pending"}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                      <div className="text-left sm:text-right">
                        <p className="text-sm text-gray-600">Total Amount</p>
                        <p className="text-lg sm:text-xl font-bold text-primary-600">₹{order.total.toLocaleString()}</p>
                      </div>
                      <button
                        onClick={(e) => handleBuyAllAgain(order, e)}
                        className="flex items-center justify-center px-3 py-2 text-sm bg-accent-500 text-black rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Buy All Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {order.items.map((item: Order["items"][number]) => (
                  <div key={`${item.id}-${(item as any).variantId || "no-variant"}`} className="flex items-center space-x-3 sm:space-x-4 bg-gray-50 rounded-lg p-3 sm:p-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                      <LazyImage
                        src={item.image || "/placeholder.svg"}
                        alt={item.name || "Product"}
                        className="w-full h-full object-cover object-center"
                        loading="lazy"
                        width={80}
                        height={80}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate text-sm sm:text-base">{item.name}</h4>
                      {/* Variant details */}
                      {((item as any).size || (item as any).color) && (
                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
                          {(item as any).size ? <span>Size: {(item as any).size}</span> : null}
                          {(item as any).color ? (
                            <span className="inline-flex items-center gap-1">
                              <span>Color:</span>
                              <span
                                className="inline-block w-3 h-3 rounded-full border"
                                style={{ backgroundColor: (item as any).colorHex || "#ccc" }}
                                title={(item as any).color}
                                aria-label={(item as any).color}
                              />
                              <span>{(item as any).color}</span>
                            </span>
                          ) : null}
                        </div>
                      )}
                      <p className="text-xs sm:text-sm text-gray-600">Quantity: {item.quantity}</p>
                      <p className="text-xs sm:text-sm text-gray-600">₹{item.price.toLocaleString()} each</p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">₹{(item.price * item.quantity).toLocaleString()}</p>
                      <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2">
                        <button
                          onClick={(e) => handleViewItem(item, e)}
                          className="flex items-center justify-center px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View Item
                        </button>
                        <button
                          onClick={(e) => handleBuyAgain(item, e)}
                          className="flex items-center justify-center px-2 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Buy Again
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onBuyAllAgain={(order) => {
          setIsModalOpen(false)
          handleBuyAllAgain(order)
        }}
      />
    </div>
  )
}

export default Orders